"use client";

import { Suspense, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function SignupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams.get("next");
  const safeNext = next?.startsWith("/") ? next : "/dashboard";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Erreur lors de la création du compte");
        return;
      }
      router.replace(safeNext);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center space-y-2">
          <Image src="/logo.svg" alt="OpusLouange" width={40} height={40} className="w-10 h-10 mx-auto" />
          <h1 className="text-2xl font-semibold tracking-tight">Créer un compte</h1>
          <p className="text-muted-foreground text-sm">Rejoignez OpusLouange gratuitement</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <p data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="name">Nom</Label>
            <Input
              id="name"
              data-testid="signup-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              placeholder="Votre nom"
              className="h-10"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="signup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="vous@exemple.com"
              className="h-10"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              id="password"
              data-testid="signup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="••••••••"
            />
            <p className="text-xs text-muted-foreground">Minimum 8 caractères</p>
          </div>
          <Button data-testid="signup-submit" type="submit" disabled={loading} className="w-full h-10 text-sm font-medium">
            {loading ? "Création…" : "Créer un compte"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Déjà un compte ?{" "}
          <Link href={next ? `/auth/login?next=${encodeURIComponent(next)}` : "/auth/login"} className="text-foreground font-medium hover:underline underline-offset-4">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}

export function SignupForm() {
  return (
    <Suspense>
      <SignupContent />
    </Suspense>
  );
}
